/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import clsx from 'clsx';

import type * as api from '@gitbook/api-client';
import type { TranslationLanguageDescriptor } from '@gitbook/app-translations';
import { Icon } from '@gitbook/spine-icons';
import {
    Emoji,
    ScrollFader,
    type TooltipProps,
    useScrollFaderController,
} from '@gitbook/spine-react';

import type { LocalizedLanguagesList } from './types';

/**
 * Tooltip content that displays the translation status for each language.
 */
export function getLocalizedTooltipProps(props: {
    disabled?: boolean;
    fallbackValue: string;
    localizedValue: api.LocalizedString;
    languages: LocalizedLanguagesList;
}): Omit<TooltipProps, 'children'> {
    const { disabled, fallbackValue, localizedValue, languages } = props;

    const hasTranslations = Object.values(localizedValue).some((value) => value);

    if (!hasTranslations) {
        return {
            disabled,
            label: 'Select language',
        };
    }

    return {
        disabled,
        disableHoverableContent: false,
        tooltip: (
            <LocalizedTooltipContent
                fallbackValue={fallbackValue}
                localizedValue={localizedValue}
                languages={languages}
            />
        ),
        className: 'w-full',
    };
}

/**
 * Scrollable container of translations for each language.
 */
function LocalizedTooltipContent(props: {
    fallbackValue: string;
    localizedValue: api.LocalizedString;
    languages: LocalizedLanguagesList;
}) {
    const { fallbackValue, localizedValue, languages } = props;
    const scrollFader = useScrollFaderController('vertical');

    return (
        <ScrollFader controller={scrollFader}>
            <div
                ref={scrollFader.ref}
                className="no-scrollbar flex max-h-60 flex-col gap-3 overflow-y-auto p-1"
            >
                <div className="flex items-center gap-2">
                    <Icon.Globe className="size-3 shrink-0" />
                    <span className="truncate text-inverted">{fallbackValue || 'No fallback'}</span>
                </div>
                {Object.entries(languages).map(
                    ([language, descriptor]: [
                        api.TranslationLanguage,
                        TranslationLanguageDescriptor,
                    ]) => {
                        const translationValue = localizedValue[language];

                        return (
                            <div key={language} className="flex items-center gap-2">
                                <Emoji code={descriptor.emoji} className="shrink-0" />
                                <span
                                    className={clsx(
                                        'truncate text-inverted',
                                        !translationValue && 'opacity-70'
                                    )}
                                >
                                    {translationValue || 'No translation'}
                                </span>
                            </div>
                        );
                    }
                )}
            </div>
        </ScrollFader>
    );
}
